import React, { forwardRef } from "react";

import { VideoChooserBlock as Value } from "@reactivated";

import { concatClassNames } from "../../utils/styles";

import styles from "./index.module.scss";

export type VideoTrack = Value["tracks"][number];

export type TrackLabelBuilder = (track: VideoTrack, video: Value) => string;

export interface Props {
    value: Value;
    attrs?: React.VideoHTMLAttributes<HTMLVideoElement>;
    /**
     * Rather than relearn the rules every time to try and hit a moving target,
     * this prop helps us codify the rules as we understand them.
     *
     * Videos still might not play if low power mode is on, extensions are
     * preventing autoplay, or if autoplay has been disabled in device settings.
     *
     * For more reading:
     *   - https://webkit.org/blog/6784/new-video-policies-for-ios/
     *   - https://webkit.org/blog/7734/auto-play-policy-changes-for-macos/
     *   - https://stackoverflow.com/q/43570460
     */
    iOSFriendlyMutedAutoPlay?: boolean;
    /**
     * Optional function that can be provided to override the format of video track labels.
     */
    getTrackLabel?: TrackLabelBuilder;
}

const getTrackLabelDefault: TrackLabelBuilder = (track) => {
    if (track.label) {
        if (track.label === track.srclang_name) {
            return `${track.label} – ${track.kind_name}`;
        }
        return `${track.label} (${track.srclang_name} – ${track.kind_name})`;
    }
    return `${track.srclang_name} – ${track.kind_name}`;
};

export const VideoChooserBlock = forwardRef<HTMLVideoElement, Props>(
    ({ getTrackLabel = getTrackLabelDefault, ...props }, ref) => {
        const attrs = props.attrs || {};
        attrs.className = concatClassNames([attrs.className, styles.video]);
        let tracksEnabled = true;

        // If not specified, default to controls being on.
        if (attrs.controls === undefined) {
            attrs.controls = true;
        }

        if (props.iOSFriendlyMutedAutoPlay === true) {
            attrs.autoPlay = true;
            /**
             * <video> elements will be allowed to autoplay without a user
             * gesture if their source media contains no audio tracks
             */
            tracksEnabled = false;
            /**
             * <video muted> elements will also be allowed to autoplay
             * without a user gesture.
             */
            attrs.muted = true;
            /**
             * On iPhone, <video playsinline> elements will now be allowed to
             * play inline, and will not automatically enter fullscreen mode
             * when playback begins.
             */
            attrs.playsInline = true;
            attrs.controls = false;
        }

        return (
            <video
                ref={ref}
                poster={props.value.poster || undefined}
                title={props.value.title}
                {...attrs}
            >
                {props.value.sources.map((source) => (
                    <source
                        key={source.url}
                        src={source.url}
                        type={source.mimetype}
                    />
                ))}
                {tracksEnabled &&
                    props.value.tracks.map((track) => (
                        <track
                            key={track.src}
                            kind={track.kind}
                            src={track.src}
                            label={getTrackLabel(track, props.value)}
                            srcLang={track.srclang}
                            default={track.sort_order === 0}
                        />
                    ))}
            </video>
        );
    },
);
